/**
 * azureConnectionsApi.ts - CRUD des connections Azure d un client.
 *
 * Une "connection Azure" = un App Registration (clientId + secret en Key Vault)
 * + une subscription cible + un tenant Entra. NIS365 utilise ces credentials
 * pour authentifier les appels ARM REST API lors du scan Azure.
 *
 * Cote backend : les secrets ne sont JAMAIS retournes en GET. Stocke dans Key
 * Vault du client, reference par secretRef = "akv:secret-name".
 */
import { apiFetch } from './apiClient';
import { DEMO_MODE } from './liveMode';
import { demoAzureConnection } from '../demo/demoData';

export interface AzureConnection {
  id:              string;          // uuid local
  name:            string;          // libelle libre ("Prod West Europe")
  tenantId:        string;          // Entra tenant GUID
  subscriptionId:  string;          // Azure subscription GUID
  appClientId:     string;          // App Reg client ID
  /**
   * Reference au secret stocke en Key Vault. Format akv:<secretName>.
   * JAMAIS le secret en clair.
   */
  secretRef?:      string;
  /** Etat de la connection (OK, expiree, secret revoque, etc.). */
  status?:         'ok' | 'error' | 'unknown';
  statusMessage?:  string;          // dernier message d erreur si status=error
  addedAt?:        string;
  addedBy?:        string;
  lastTestAt?:     string | null;
  lastScanAt?:     string | null;
}

export interface AzureConnectionInput {
  name:            string;
  tenantId:        string;
  subscriptionId:  string;
  appClientId:     string;
  /** Secret en clair (transmis HTTPS, stocke en Key Vault par le backend). */
  clientSecret:    string;
}

export interface AzureConnectionTestResult {
  success:        boolean;
  /** Donnees retournees par Azure : nom subscription, tenant, roles. */
  subscriptionName?: string;
  tenantName?:    string;
  rolesGranted?:  string[];        // ex: ['Reader']
  error?:         string;
  hint?:          string;
}

export const azureConnectionsApi = {
  /** Liste les connections du client (secrets occultes). */
  list: () => DEMO_MODE
    ? Promise.resolve({ connections: [demoAzureConnection()] })
    : apiFetch<{ connections: AzureConnection[] }>('/clients/azure-connections'),

  /** Detail d une connection (secret occulte). */
  get: (id: string) => apiFetch<AzureConnection>(`/clients/azure-connections/${id}`),

  /** Cree une nouvelle connection. Le secret est stocke en Key Vault par le backend. */
  create: (input: AzureConnectionInput) =>
    apiFetch<AzureConnection>('/clients/azure-connections', {
      method: 'POST',
      body:   JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
    }),

  /** Met a jour une connection existante (nom, ou rotation secret). */
  update: (id: string, input: Partial<AzureConnectionInput>) =>
    apiFetch<AzureConnection>(`/clients/azure-connections/${id}`, {
      method: 'PATCH',
      body:   JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
    }),

  /** Supprime une connection (purge secret du Key Vault). */
  remove: (id: string) =>
    apiFetch<{ removed: boolean }>(`/clients/azure-connections/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Test live de la connection : appelle ARM avec les credentials pour valider.
   * Retourne le nom de la subscription, les roles RBAC presents, etc.
   */
  test: (id: string) =>
    apiFetch<AzureConnectionTestResult>(`/clients/azure-connections/${id}/test`, {
      method: 'POST',
    }),

  /**
   * Test "dry-run" sans persister - utilise par le wizard pour valider les
   * credentials avant de creer la connection. Body = AzureConnectionInput.
   */
  testDryRun: (input: AzureConnectionInput) =>
    apiFetch<AzureConnectionTestResult>('/clients/azure-connections/test-dry-run', {
      method: 'POST',
      body:   JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
    }),
};
